/**
 * Verlauf — Inventar 2.0, S7b.
 *
 * WHAT THIS FILE DOES NOT TOUCH. The timeline items themselves come from
 * components/admin/AuditLogVisual (AdminAuditTimeline / AdminAuditTimelineEntry),
 * which AdminUnitDetailPage, AdminApplicationDetailPage, AdminTenantDetailPage,
 * PlatformAuditLogsPage and TenantHistoryBlock also render. Restyling those
 * would silently redesign five other pages, so S7b leaves them alone and only
 * tokenizes what this page owns: the card around them, the heading row, the
 * count badge, the three states, the scroll area, and the two presentation
 * props the page already passes per entry (dot colour and meta line).
 *
 * SEMANTICS UNCHANGED. Same single parent stream (entity_type=inventory_item),
 * same server order — nothing is re-sorted or grouped here — same actor, same
 * count wording, same empty and error strings.
 *
 * THE HEADING NESTING IS A CONTRACT. Both S6a suites resolve this surface as
 * getByRole("heading", { name: "Verlauf" }).parentElement.parentElement, i.e.
 * heading → header row → card. That is exactly the nesting below; the <section>
 * replaces the old <div> so the card is also a named landmark, which is
 * additive.
 *
 * The scrollbar no longer rides on .inventory-detail-verlauf-scroll. That class
 * existed only so index.css could reach it, and S7b's whole point is to retire
 * that block — so the thin scrollbar is expressed inline in tokens instead.
 */
import React from "react";

import { Pencil, Plus, Trash2 } from "lucide-react";

import {
  AdminAuditTimeline,
  AdminAuditTimelineEntry,
} from "../../../components/admin/AuditLogVisual";
import { auditActorDisplay, formatAuditLog } from "../../../utils/auditDisplay";

/**
 * Icon per event, from the action the row already carries.
 *
 * Purely a rendering of `log.action` — no new event, no re-sorting, no extra
 * request, and formatAuditLog keeps producing the summary and the changes. An
 * unknown action falls back to the edit glyph rather than to nothing, so the
 * rail never has a hole in it.
 */
function actionIcon(action) {
  const a = String(action || "").toLowerCase();
  if (a === "create") return { icon: Plus, tile: "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" };
  if (a === "delete") return { icon: Trash2, tile: "border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-400" };
  return { icon: Pencil, tile: "border-border bg-muted text-muted-foreground" };
}

/* Taller than the old 320px: the card is no longer shoulder to shoulder with
   a full operative stack, so it can show more of the history before the
   scroller takes over. */
const SCROLLER =
  "mt-3 max-h-[420px] overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent]";

export default function InventoryTimeline({ logs, loading, error, unitById }) {
  const count = loading ? "…" : logs.length === 1 ? "1 Eintrag" : `${logs.length} Einträge`;
  return (
    /* S9: the aside is a right-hand column again, but only from 2xl — see the
       measurement in AdminInventoryDetailPage. Below that it stacks under the
       main column at full width. 320px is the repo's own smallest aside step
       (CalendarSidePanel.jsx:57); at 1536 that leaves the main column ~830px,
       and from ~1900px the Zuordnungen table needs no scroller beside it.
       Still an <aside>: chronological audit reading is related to the article,
       not part of operating on it. */
    <aside className="w-full min-w-0 2xl:w-[320px] 2xl:shrink-0">
      <section className="rounded-[10px] border border-border bg-card p-4" aria-label="Verlauf">
        <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
          <h2 className="m-0 text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
            Verlauf
          </h2>
          <span className="inline-flex max-w-full items-center whitespace-nowrap rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        </div>
        <p className="m-0 mt-3 text-[11px] leading-snug text-muted-foreground">
          Verlauf aus dem Audit-Log (nur Lesen, neueste zuerst): Erstellung, Bearbeitung,
          Zuordnungen.
        </p>
        <div className={SCROLLER}>
          {loading ? (
            <p className="m-0 text-[13px] font-medium text-muted-foreground">Lade Verlauf …</p>
          ) : error ? (
            <p className="m-0 text-[13px] font-medium text-destructive">{error}</p>
          ) : logs.length === 0 ? (
            <p className="m-0 rounded-[8px] border border-dashed border-border bg-muted px-3 py-5 text-center text-[13px] font-medium text-muted-foreground">
              Keine Einträge im Verlauf.
            </p>
          ) : (
            <AdminAuditTimeline className="mt-1 ml-3">
              {logs.map((log) => {
                const { summary, changes } = formatAuditLog(log, {
                  entityType: "inventory_item",
                  unitById,
                });
                const { icon, tile } = actionIcon(log.action);
                return (
                  <AdminAuditTimelineEntry
                    key={log.id}
                    summary={summary}
                    changes={changes}
                    createdAt={log.created_at}
                    actor={auditActorDisplay(log)}
                    action={log.action}
                    icon={icon}
                    iconTileClassName={tile}
                    metaClassName="text-[11px] leading-snug text-muted-foreground"
                  />
                );
              })}
            </AdminAuditTimeline>
          )}
        </div>
      </section>
    </aside>
  );
}
